import { useMemo, useState } from "react";
import { Card } from "react-bootstrap";
import {
  useGetVaSettingsQuery,
  useUpdateVaPaymentMethodMutation,
  useChangeVaPasswordMutation,
} from "../../app/vaApi/vaApi";
import CommonModal from "../../components/shared/modalLayout/CommonModal";
import InputWithLabel from "../../components/shared/fields/InputWithLabel";
import CustomDropdown from "../../components/shared/dropdown/CustomDropdown";
import Button from "../../components/shared/buttons/button";
import Loader from "../../components/shared/loader/Loader";
import PasswordInput, {
  isValidLoginPassword,
} from "../../components/va-distributors/PasswordInput";
import { formatMaskedBankAccount } from "../../helpers/vaSettingsUtils";
import { showToast } from "../../helpers/Utils";
import { useAuth } from "../auth/AuthContext";
import { hasRole } from "../../helpers/roleNames";
import { P } from "../../utils/permissions";
import "./vaDashboardSettings.css";

const ACCOUNT_TYPE_OPTIONS = [
  { value: "checking", label: "Checking" },
  { value: "savings", label: "Savings" },
];

const EMPTY_PAYMENT_FORM = {
  accountHolderName: "",
  accountType: "checking",
  routingNumber: "",
  accountNumber: "",
};

const EMPTY_PASSWORD_FORM = {
  password: "",
  confirmPassword: "",
};

function digitsOnly(value) {
  return String(value || "").replace(/\D/g, "");
}

function SettingsPencilIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M14.8 3.2C15.3 2.7 16.1 2.7 16.6 3.2L17.6 4.2C18.1 4.7 18.1 5.5 17.6 6L6.8 16.8L3.8 17.6L4.6 14.6L14.8 3.2Z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function VaDashboardSettings() {
  const { roles, hasPermission } = useAuth();
  const securityOnly =
    hasRole(roles, "va-admin") && !hasPermission(P.ORDERS_READ_ASSIGNED);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState(EMPTY_PAYMENT_FORM);
  const [paymentErrors, setPaymentErrors] = useState({});

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState(EMPTY_PASSWORD_FORM);
  const [passwordErrors, setPasswordErrors] = useState({});

  const {
    data: settingsData,
    isLoading: settingsLoading,
    isError: settingsError,
    refetch: refetchSettings,
  } = useGetVaSettingsQuery(undefined, {
    skip: securityOnly,
    refetchOnMountOrArgChange: true,
  });

  const [updatePaymentMethod, { isLoading: isSavingPayment }] =
    useUpdateVaPaymentMethodMutation();
  const [changePassword, { isLoading: isSavingPassword }] =
    useChangeVaPasswordMutation();

  const settings = settingsData?.data;
  const paymentMethod = settings?.paymentMethod;

  const maskedBank = useMemo(
    () => formatMaskedBankAccount(paymentMethod?.last4),
    [paymentMethod?.last4]
  );

  const openPaymentEditor = () => {
    const accountNumber = String(paymentMethod?.accountNumber || "").startsWith(
      "****"
    )
      ? ""
      : paymentMethod?.accountNumber || "";
    const routingNumber = String(paymentMethod?.routingNumber || "").startsWith(
      "****"
    )
      ? ""
      : paymentMethod?.routingNumber || "";

    const accountHolderName =
      String(paymentMethod?.accountHolderName || "").trim() ||
      [
        paymentMethod?.accountHolderFirstName,
        paymentMethod?.accountHolderLastName,
      ]
        .map((part) => String(part || "").trim())
        .filter(Boolean)
        .join(" ");

    setPaymentForm({
      accountHolderName,
      accountType: paymentMethod?.accountType || "checking",
      routingNumber,
      accountNumber,
    });
    setPaymentErrors({});
    setShowPaymentModal(true);
  };

  const openPasswordEditor = () => {
    setPasswordForm(EMPTY_PASSWORD_FORM);
    setPasswordErrors({});
    setShowPasswordModal(true);
  };

  const validatePayment = () => {
    const errors = {};
    if (!paymentForm.accountHolderName.trim()) {
      errors.accountHolderName = "Name is required";
    }
    if (!["checking", "savings"].includes(paymentForm.accountType)) {
      errors.accountType = "Select Checking or Savings";
    }
    const routing = digitsOnly(paymentForm.routingNumber);
    if (routing.length !== 9) {
      errors.routingNumber = "Routing number must be 9 digits";
    }
    const account = digitsOnly(paymentForm.accountNumber);
    if (account.length < 4 || account.length > 17) {
      errors.accountNumber = "Account number must be 4–17 digits";
    }
    setPaymentErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validatePassword = () => {
    const errors = {};
    const password = passwordForm.password.trim();
    const confirmPassword = passwordForm.confirmPassword.trim();

    if (!password) {
      errors.password = "Password is required";
    } else if (!isValidLoginPassword(password)) {
      errors.password =
        "Password must be 8–50 characters with upper, lower, number, and special character.";
    }

    if (!confirmPassword) {
      errors.confirmPassword = "Confirm password is required";
    } else if (password !== confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSavePayment = async () => {
    if (!validatePayment()) return;

    const accountNumber = digitsOnly(paymentForm.accountNumber);
    const routingNumber = digitsOnly(paymentForm.routingNumber);
    const accountHolderName = paymentForm.accountHolderName.trim();

    try {
      await updatePaymentMethod({
        accountHolderName,
        routingNumber,
        accountNumber,
        accountType: paymentForm.accountType || "checking",
      }).unwrap();
      showToast("Payment method saved", "success");
      setShowPaymentModal(false);
      setPaymentForm(EMPTY_PAYMENT_FORM);
      refetchSettings();
    } catch (err) {
      showToast(
        err?.data?.message || "Failed to save payment method",
        "error"
      );
    }
  };

  const handleSavePassword = async () => {
    if (!validatePassword()) return;

    try {
      await changePassword({
        password: passwordForm.password.trim(),
        confirmPassword: passwordForm.confirmPassword.trim(),
      }).unwrap();
      showToast("Password updated successfully", "success");
      setShowPasswordModal(false);
      setPasswordForm(EMPTY_PASSWORD_FORM);
      setPasswordErrors({});
    } catch (err) {
      const field = err?.data?.field;
      const msg = err?.data?.message || "Failed to update password";
      if (field) {
        setPasswordErrors((prev) => ({ ...prev, [field]: msg }));
      } else {
        showToast(msg, "error");
      }
    }
  };

  return (
    <div className="va-dashboard-settings">
      <div className="va-dashboard-settings__header">
        <h4 className="va-dashboard-settings__title">Settings</h4>
      </div>

      {!securityOnly && (
      <Card className="va-dashboard-settings__card">
        <Card.Body>
          <div className="va-dashboard-settings__card-head">
            <h6 className="va-dashboard-settings__card-title mb-1">Payment methods</h6>
          </div>

          {settingsLoading ? (
            <div className="py-3 d-flex justify-content-center">
              <Loader />
            </div>
          ) : settingsError ? (
            <div className="py-2">
              <p className="text-danger small mb-2">Failed to load settings</p>
              <Button label="Retry" className="btn-grey" onClick={refetchSettings} />
            </div>
          ) : (
            <div className="va-dashboard-settings__payment-list">
              {paymentMethod?.isComplete && maskedBank ? (
                <div className="va-dashboard-settings__payment-row">
                  <div className="va-dashboard-settings__payment-text">
                    <span className="va-dashboard-settings__payment-brand">
                      Bank account
                    </span>
                    <span className="va-dashboard-settings__payment-masked">
                      {" "}
                      {maskedBank}
                    </span>
                  </div>

                  <button
                    type="button"
                    className="va-dashboard-settings__icon-btn"
                    aria-label="Edit payment method"
                    onClick={openPaymentEditor}
                  >
                    <SettingsPencilIcon />
                  </button>
                </div>
              ) : (
                <div className="va-dashboard-settings__payment-row">
                  <div className="va-dashboard-settings__payment-text">
                    <span className="va-dashboard-settings__payment-brand">
                      No bank account on file
                    </span>
                  </div>
                  <Button
                    label="Add"
                    className="btn-grey"
                    onClick={openPaymentEditor}
                  />
                </div>
              )}
            </div>
          )}
        </Card.Body>
      </Card>
      )}

      <Card className={`va-dashboard-settings__card${securityOnly ? "" : " mt-3"}`}>
        <Card.Body>
          <div className="va-dashboard-settings__card-head mb-2">
            <h6 className="va-dashboard-settings__card-title mb-0">Security</h6>
          </div>
          <div className="va-dashboard-settings__payment-row">
            <div className="va-dashboard-settings__payment-text">
              <span className="va-dashboard-settings__payment-brand">
                Password
              </span>
              <p className="va-dashboard-settings__card-subtitle text-muted mb-0 mt-1">
                Change the password you use to sign in.
              </p>
            </div>
            <Button
              label="Change"
              className="btn-grey"
              onClick={openPasswordEditor}
            />
          </div>
        </Card.Body>
      </Card>

      <CommonModal
        show={showPaymentModal}
        setShow={setShowPaymentModal}
        title="Bank account"
        centered
        className="modal-md"
        onConfirm={handleSavePayment}
        confirmLabel="Save"
        confirmLoading={isSavingPayment}
        confirmDisabled={isSavingPayment}
        cancelDisabled={isSavingPayment}
      >
        <InputWithLabel
          label="Name"
          name="accountHolderName"
          value={paymentForm.accountHolderName}
          onChange={(e) =>
            setPaymentForm((prev) => ({
              ...prev,
              accountHolderName: e.target.value,
            }))
          }
          placeholder="Account holder name"
          required
        />
        {paymentErrors.accountHolderName && (
          <p className="text-danger small mt-n2">
            {paymentErrors.accountHolderName}
          </p>
        )}

        <div className="mb-3">
          <label className="cmn_label d-block">
            Type <span className="text-danger">*</span>
          </label>
          <CustomDropdown
            label="Select type"
            value={paymentForm.accountType}
            onSelect={(opt) =>
              setPaymentForm((prev) => ({
                ...prev,
                accountType: opt.value,
              }))
            }
            options={ACCOUNT_TYPE_OPTIONS}
            error={!!paymentErrors.accountType}
            className="w-100"
          />
          {paymentErrors.accountType && (
            <p className="text-danger small">{paymentErrors.accountType}</p>
          )}
        </div>

        <InputWithLabel
          label="Routing number"
          name="routingNumber"
          value={paymentForm.routingNumber}
          onChange={(e) =>
            setPaymentForm((prev) => ({
              ...prev,
              routingNumber: digitsOnly(e.target.value).slice(0, 9),
            }))
          }
          placeholder="9-digit routing number"
          required
        />
        {paymentErrors.routingNumber && (
          <p className="text-danger small mt-n2">{paymentErrors.routingNumber}</p>
        )}

        <InputWithLabel
          label="Account number"
          name="accountNumber"
          value={paymentForm.accountNumber}
          onChange={(e) =>
            setPaymentForm((prev) => ({
              ...prev,
              accountNumber: e.target.value,
            }))
          }
          placeholder="Enter account number"
          required
        />
        {paymentErrors.accountNumber && (
          <p className="text-danger small mt-n2">{paymentErrors.accountNumber}</p>
        )}
      </CommonModal>

      <CommonModal
        show={showPasswordModal}
        setShow={setShowPasswordModal}
        title="Change password"
        centered
        className="modal-md"
        onConfirm={handleSavePassword}
        confirmLabel="Save"
        confirmLoading={isSavingPassword}
        confirmDisabled={isSavingPassword}
        cancelDisabled={isSavingPassword}
      >
        <div className="mb-3">
          <label className="cmn_label d-block">
            New password <span className="text-danger">*</span>
          </label>
          <PasswordInput
            name="password"
            value={passwordForm.password}
            onChange={(e) => {
              const password = e.target.value;
              setPasswordForm((prev) => ({ ...prev, password }));
              setPasswordErrors((prev) => {
                if (!prev.password) return prev;
                const next = { ...prev };
                delete next.password;
                return next;
              });
            }}
            placeholder="Enter new password"
            error={!!passwordErrors.password}
            autoComplete="new-password"
          />
          {passwordErrors.password && (
            <p className="text-danger small">{passwordErrors.password}</p>
          )}
        </div>

        <div className="mb-1">
          <label className="cmn_label d-block">
            Confirm password <span className="text-danger">*</span>
          </label>
          <PasswordInput
            name="confirmPassword"
            value={passwordForm.confirmPassword}
            onChange={(e) => {
              const confirmPassword = e.target.value;
              setPasswordForm((prev) => ({ ...prev, confirmPassword }));
              setPasswordErrors((prev) => {
                if (!prev.confirmPassword) return prev;
                const next = { ...prev };
                delete next.confirmPassword;
                return next;
              });
            }}
            placeholder="Confirm new password"
            error={!!passwordErrors.confirmPassword}
            autoComplete="new-password"
          />
          {passwordErrors.confirmPassword && (
            <p className="text-danger small">{passwordErrors.confirmPassword}</p>
          )}
        </div>
      </CommonModal>
    </div>
  );
}
